// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Json from "./Json.res.js";
import * as Future from "rescript-future/src/Future.res.js";
import * as Exceljs from "exceljs";
import * as Papaparse from "papaparse";
import * as Belt_Array from "@rescript/runtime/lib/es6/Belt_Array.js";
import * as Stdlib_JSON from "@rescript/runtime/lib/es6/Stdlib_JSON.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as FuturePromise from "rescript-future/src/FuturePromise.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as Primitive_exceptions from "@rescript/runtime/lib/es6/Primitive_exceptions.js";

function makeCsvPlainText(delimiterOpt, rows) {
  let delimiter = delimiterOpt !== undefined ? delimiterOpt : ";";
  let rows$1;
  try {
    rows$1 = Papaparse.unparse(rows, {
      delimiter: delimiter,
      newline: "\n"
    });
  } catch (exn) {
    return {
      TAG: "Error",
      _0: undefined
    };
  }
  let lines = rows$1.split("\n");
  let maxCols = Stdlib_Array.reduce(lines, 0, (acc, line) => {
    let count = line.split(delimiter).length;
    if (count > acc) {
      return count;
    } else {
      return acc;
    }
  });
  let entries = lines.map(line => {
    let count = line.split(delimiter).length;
    return line + delimiter.repeat(maxCols - count | 0);
  }).map(prim => prim.trim()).join("\n") + "\n";
  return {
    TAG: "Ok",
    _0: entries
  };
}

function makeCsvBlobFromPlainText(plainText) {
  return new Blob([
    new Uint8Array([
      239,
      187,
      191
    ]),
    plainText
  ], {
    type: "text/csv;charset=utf-8"
  });
}

function makeExcelBlob(rows, worksheetName) {
  try {
    let workbook = new Exceljs.Workbook();
    let worksheet = workbook.addWorksheet(worksheetName);
    for (let index = 0, index_finish = rows.length; index < index_finish; ++index) {
      let row = rows[index];
      if (row !== undefined) {
        worksheet.addRow(row);
      }
    }
    return Future.mapError(FuturePromise.fromPromise(workbook.xlsx.writeBuffer().then(data => Promise.resolve(new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })))), undefined, param => {});
  } catch (exn) {
    return Future.value({
      TAG: "Error",
      _0: undefined
    });
  }
}

function parseExceljsXlsxFile(headerIndexOffsetOpt, file) {
  let headerIndexOffset = headerIndexOffsetOpt !== undefined ? headerIndexOffsetOpt : 1;
  let workbook = new Exceljs.Workbook();
  let xlsx = workbook.xlsx;
  let findWorksheet = (_indexOpt, workbook) => {
    while (true) {
      let indexOpt = _indexOpt;
      let index = indexOpt !== undefined ? indexOpt : 1;
      if (index > 10) {
        let NotFoundWorksheet = /* @__PURE__ */Primitive_exceptions.create("NotFoundWorksheet");
        return Promise.reject({
          RE_EXN_ID: NotFoundWorksheet
        });
      }
      let worksheet = workbook.getWorksheet(index);
      if (!(worksheet == null)) {
        return Promise.resolve(worksheet);
      }
      _indexOpt = index + 1 | 0;
      continue;
    };
  };
  let processWorkbook = async data => {
    let workbook = await xlsx.load(data);
    let worksheet = await findWorksheet(undefined, workbook);
    let rows = {
      contents: []
    };
    let workingHeaders = worksheet.getRow(headerIndexOffset).values.slice(1);
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= headerIndexOffset) {
        return;
      }
      let dict = {};
      let values = row.values.slice(1);
      workingHeaders.forEach((header, index) => {
        let header$1 = Stdlib_Option.flatMap(header, Stdlib_JSON.Decode.string);
        let match = values[index];
        let cell;
        if (match !== undefined) {
          let json = Primitive_option.valFromOption(match);
          if (json !== undefined) {
            let resultFromFormulaJson = Stdlib_Option.flatMap(Json.decodeDict(json), dict => dict["result"]);
            cell = resultFromFormulaJson !== undefined ? resultFromFormulaJson : json;
          } else {
            cell = null;
          }
        } else {
          cell = null;
        }
        if (header$1 === undefined) {
          return;
        }
        if (header$1 === "") {
          return;
        }
        dict[header$1] = cell;
      });
      rows.contents.push(dict);
    });
    return rows.contents.map(prim => prim);
  };
  return Future.mapError(FuturePromise.fromPromise(file.arrayBuffer().then(processWorkbook)), undefined, param => {});
}

function parseCsvFile(file) {
  return Future.make(resolve => {
    let data = [];
    let step = param => {
      data.push(param.data);
    };
    let complete = param => resolve({
      TAG: "Ok",
      _0: data
    });
    let error = param => resolve({
      TAG: "Error",
      _0: undefined
    });
    try {
      let config_delimiter = ";";
      let config_skipEmptyLines = "greedy";
      let config = {
        step: step,
        complete: complete,
        error: error,
        delimiter: config_delimiter,
        skipEmptyLines: config_skipEmptyLines,
        worker: true
      };
      Papaparse.parse(file, config);
    } catch (exn) {
      resolve({
        TAG: "Error",
        _0: undefined
      });
    }
  });
}

function CsvParserAndDecoder(Config) {
  let make = file => Future.make(resolve => {
    let data = {
      contents: []
    };
    let errors = {
      contents: []
    };
    let index = {
      contents: 0
    };
    let step = param => {
      let entry = param.data;
      let entryIndex = {
        contents: 0
      };
      let valid = entry.every(cellValue => {
        let cell = Config.cellFromEntryIndex(entryIndex.contents, cellValue);
        let error = Config.validateCellFromLabel(cell);
        entryIndex.contents = entryIndex.contents + 1 | 0;
        if (error.TAG === "Ok") {
          return true;
        }
        errors.contents = Belt_Array.concatMany([
          errors.contents,
          [{
              TAG: "InvalidCell",
              entryIndex: entryIndex.contents,
              rowIndex: index.contents + 1 | 0,
              message: error._0,
              value: cellValue
            }]
        ]);
        return false;
      });
      if (valid) {
        data.contents = Belt_Array.concatMany([
          data.contents,
          [Config.rowFromEntry(entry)]
        ]);
      }
      index.contents = index.contents + 1 | 0;
    };
    let complete = param => resolve({
      TAG: "Ok",
      _0: [
        data.contents,
        errors.contents
      ]
    });
    let error = param => resolve({
      TAG: "Error",
      _0: undefined
    });
    let config_delimiter = ";";
    let config_skipEmptyLines = true;
    let config = {
      step: step,
      complete: complete,
      error: error,
      delimiter: config_delimiter,
      skipEmptyLines: config_skipEmptyLines,
      worker: true
    };
    try {
      Papaparse.parse(file, config);
    } catch (exn) {
      resolve({
        TAG: "Error",
        _0: undefined
      });
    }
  });
  return {
    make: make
  };
}

let csvFileExtension = "csv";

let excelFileExtension = "xlsx";

export {
  csvFileExtension,
  makeCsvPlainText,
  makeCsvBlobFromPlainText,
  excelFileExtension,
  makeExcelBlob,
  parseExceljsXlsxFile,
  parseCsvFile,
  CsvParserAndDecoder,
}
/* exceljs Not a pure module */
